import { pickT } from "@repo/shared";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { patch } from "@/lib/api/_client";
import { log } from "@/lib/logger/browser-logger";
import type {
  ColumnConfig,
  ComponentStyleType,
  FlowPageLayout,
  LayoutConfig,
} from "@/lib/types/flow";
import { T } from "./column-settings-dialog.i18n";

type ColumnSettingsDialogProps = {
  open: boolean;
  flowId: string;
  flowLanguage: string;
  layout: FlowPageLayout | null;
  column: "left" | "right";
  onClose: (success: boolean) => void;
};

export function ColumnSettingsDialog({
  open,
  flowId,
  flowLanguage,
  layout,
  column,
  onClose,
}: ColumnSettingsDialogProps) {
  const t = pickT(T, flowLanguage);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Column styling state
  const [columnStyle, setColumnStyle] = useState<ComponentStyleType>("none");
  const [columnTitle, setColumnTitle] = useState("");
  const [columnDescription, setColumnDescription] = useState("");

  useEffect(() => {
    if (open && layout) {
      // Parse existing config
      try {
        const config: LayoutConfig = layout.config ? JSON.parse(layout.config) : {};

        // Load column-specific settings
        const columnConfig = column === "left" ? config.leftColumn : config.rightColumn;
        if (columnConfig) {
          setColumnStyle(columnConfig.style || "none");
          setColumnTitle(columnConfig.title || "");
          setColumnDescription(columnConfig.description || "");
        } else {
          setColumnStyle("none");
          setColumnTitle("");
          setColumnDescription("");
        }
      } catch (err) {
        log.error({ err }, "failed to parse flow column layout config");
        setColumnStyle("none");
        setColumnTitle("");
        setColumnDescription("");
      }
    } else {
      // Reset when closing
      setColumnStyle("none");
      setColumnTitle("");
      setColumnDescription("");
    }
    setError(null);
  }, [layout, column, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!layout) return;

    setLoading(true);
    setError(null);

    try {
      // Parse existing config to preserve other settings
      let existingConfig: LayoutConfig = {};
      try {
        existingConfig = layout.config ? JSON.parse(layout.config) : {};
      } catch {
        existingConfig = {};
      }

      // Build column config
      const columnConfig: ColumnConfig = {
        style: columnStyle,
        title: columnTitle.trim() || undefined,
        description: columnDescription.trim() || undefined,
      };

      // Update the appropriate column
      const newConfig: LayoutConfig = {
        ...existingConfig,
        [column === "left" ? "leftColumn" : "rightColumn"]: columnConfig,
      };

      await patch(`/internal/flows/${flowId}/layouts/${layout.id}`, {
        config: JSON.stringify(newConfig),
      });

      onClose(true);
    } catch (_err) {
      setError(t.failedToUpdate);
    } finally {
      setLoading(false);
    }
  };

  const columnLabel = column === "left" ? t.left : t.right;

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {columnLabel} {t.columnSettings}
          </DialogTitle>
          <DialogDescription>
            {t.configureDescription} {columnLabel.toLowerCase()} {t.column}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="column-style">{t.style}</Label>
              <Select
                value={columnStyle}
                onValueChange={(v) => setColumnStyle(v as ComponentStyleType)}
              >
                <SelectTrigger id="column-style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.styleNone}</SelectItem>
                  <SelectItem value="border">{t.styleBorder}</SelectItem>
                  <SelectItem value="box">{t.styleBox}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t.styleDesc}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="column-title">{t.titleOptional}</Label>
              <Input
                id="column-title"
                value={columnTitle}
                onChange={(e) => setColumnTitle(e.target.value)}
                placeholder={t.titlePlaceholder}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="column-description">{t.descriptionOptional}</Label>
              <Textarea
                id="column-description"
                value={columnDescription}
                onChange={(e) => setColumnDescription(e.target.value)}
                placeholder={t.descriptionPlaceholder}
                rows={3}
                disabled={loading}
              />
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
